/**
 * Hook for computing H3 hex density from vehicle positions.
 *
 * Uses vanilla Zustand subscribe() to avoid React re-renders on every
 * vehicle position update. Recomputes hex buckets on a 2-second timer
 * when the vehicle map reference changes.
 */

import { useEffect, useRef, useState } from 'react';
import { useVehicleStore } from '../stores/vehicleStore';
import { useSettingsStore } from '../stores/settingsStore';
import { aggregateToHexBuckets, hexBucketsToGeoJSON, HEX_DISPLAY_RESOLUTION } from '../utils/hexDensity';

const RECOMPUTE_INTERVAL_MS = 2000;

const EMPTY_GEOJSON: GeoJSON.FeatureCollection = {
  type: 'FeatureCollection',
  features: [],
};

/**
 * Returns a GeoJSON FeatureCollection of hex density polygons,
 * recomputed every 2 seconds from vehicle positions.
 *
 * @param enabled Whether hex density computation is active
 */
export function useHexDensity(enabled: boolean): GeoJSON.FeatureCollection {
  const [geojson, setGeojson] = useState<GeoJSON.FeatureCollection>(EMPTY_GEOJSON);
  const vehiclesRef = useRef(useVehicleStore.getState().vehicles);
  const resolution = useSettingsStore((state) => state.hexResolution) ?? HEX_DISPLAY_RESOLUTION;

  // Subscribe to vehicle store changes outside React render cycle
  useEffect(() => {
    if (!enabled) return;

    const unsub = useVehicleStore.subscribe(
      (state) => {
        vehiclesRef.current = state.vehicles;
      }
    );
    return unsub;
  }, [enabled]);

  // Recompute hex buckets on a timer
  useEffect(() => {
    if (!enabled) {
      setGeojson(EMPTY_GEOJSON);
      return;
    }

    const recompute = async () => {
      const vehicles = vehiclesRef.current;
      if (vehicles.size === 0) {
        setGeojson(EMPTY_GEOJSON);
        return;
      }

      const positions = Array.from(vehicles.values()).map((v) => ({
        latitude: v.targetLatitude,
        longitude: v.targetLongitude,
        journeyId: v.journey_gid,
      }));

      const buckets = await aggregateToHexBuckets(positions, resolution);
      setGeojson(hexBucketsToGeoJSON(buckets));
    };

    // Compute immediately, then on interval
    recompute();
    const interval = setInterval(recompute, RECOMPUTE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [enabled, resolution]);

  return geojson;
}
